<template>
    <div
        class="bill-item-option"
        :class="{
            'has-discount': option.hasDiscount,
            'has-removable-discount': option.hasDiscount && allowDiscountRemoval,
        }"
    >
        <div class="bill-item-option-details" :class="{ inactive: inactive }">
            <div class="bill-item-option-name">
                <div class="quantity" v-if="option.quantity > 1">{{ option.quantity }}x</div>
                {{ getNameInLocationLanguage(option, props.showInternalNames) }}<ItemIcons v-if="option.articleId" :articleId="option.articleId" />
            </div>
            <div class="bill-item-option-price">
                <ItemPrice
                    v-if="option.unitPrice != 0"
                    class="bill-item-option-price"
                    :hasDiscount="option.hasDiscount"
                    :quantity="nextMultiplier"
                    :unitPrice="option.unitPrice"
                    :unitPriceAfterDiscount="option.unitPriceAfterDiscount"
                    :discountType="option.lastDiscountType"
                    @click="onPriceClick"
                />
            </div>
        </div>
        <div class="subs" v-if="option.options.length">
            <BillItemOptionGroup
                v-for="optionGroup in option.options"
                :key="optionGroup.id"
                :optionGroup="optionGroup"
                :multiplier="nextMultiplier"
                :inactive="inactive"
                :showInternalNames="showInternalNames"
                :allowDiscountRemoval="allowDiscountRemoval"
                :forceDisplayGroupName="forceDisplayGroupName"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import type { IBillItemOption } from "@/data/model/Bill";
    import { useBillStore } from "@/data/stores/bill";
    import { getNameInLocationLanguage } from "@/shared/helpers/ItemNameHelper";
    import { translations } from "@/shared/localization/translations";
    import { computed } from "vue";

    import BillItemOptionGroup from "./BillItemOptionGroup.vue";
    import ItemIcons from "./ItemIcons.vue";
    import ItemPrice from "./ItemPrice.vue";

    const bill = useBillStore();

    const props = defineProps<{
        option: IBillItemOption;
        multiplier: number;
        inactive?: boolean;
        showInternalNames: boolean;
        allowDiscountRemoval: boolean;
        forceDisplayGroupName?: boolean;
    }>();

    const nextMultiplier = computed(() => props.multiplier * props.option.quantity);

    function onPriceClick(e: Event) {
        if (!props.allowDiscountRemoval) return;
        if (!props.option.hasDiscount) return;

        if (props.option.hasProcessedItemLevelDiscount) {
            e.stopPropagation();
            Popup.show({
                message: translations.discounts.removeAllItemDiscountsBlockedPrompt,
                uniqueId: "itemLevelDiscountsProcessedPopup",
            });
            return;
        }

        e.stopPropagation();
        bill.showConfirmItemDiscountsRemovalPopup(props.option);
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_variables.scss";

    .bill-item-option {
        font-size: 12px;
        line-height: 1.666em;

        > .bill-item-option-details {
            display: flex;
            flex-direction: row;
            flex-wrap: nowrap;
            justify-content: space-between;
            color: colors.$fontColor;

            &.inactive {
                color: colors.$lightFontColor;
            }

            > .bill-item-option-name {
                > .quantity {
                    display: inline-block;
                    vertical-align: top;
                    padding-right: 0.25em;
                    font-weight: 600;
                }

                flex-grow: 1;
            }

            > .bill-item-option-price {
                flex: 0 0 4.666em;
                text-align: right;
            }
        }

        &.has-removable-discount {
            .bill-item-option-price {
                > .has-discount {
                    cursor: pointer;
                    transition: opacity 0.2s variables.$easeOutExpo;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }

        > .subs {
            padding: 0.1666em 0;
            padding-left: 1em;
        }
    }
</style>
